import { useState } from "react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Store, Shield, CheckCircle } from "lucide-react";
import type { AppMode } from "@/types/convex";

interface LevelAppsSettingsContentProps {
  levelId?: string;
  appMode: AppMode;
  onAppModeChange: (mode: AppMode) => Promise<void>;
}

export const LevelAppsSettingsContent = ({
  levelId,
  appMode,
  onAppModeChange
}: LevelAppsSettingsContentProps) => {
  const [showBlacklistWarning, setShowBlacklistWarning] = useState(false);
  const [pendingMode, setPendingMode] = useState<AppMode | null>(null);

  const handleModeChange = (value: string) => {
    const mode = value as AppMode;
    
    // Show warning when switching to blacklist (Apple App Store)
    if (mode === "blacklist" && appMode !== "blacklist") {
      setPendingMode(mode);
      setShowBlacklistWarning(true);
    } else {
      onAppModeChange(mode);
    }
  };

  const confirmBlacklistSwitch = () => {
    if (pendingMode) {
      onAppModeChange(pendingMode);
    }
    setShowBlacklistWarning(false);
    setPendingMode(null);
  };

  const getModeDescription = (mode: AppMode): string => {
    switch (mode) {
      case "categories":
        return "Kosher App Store enforces app filtering based on categories, whitelists, and blacklists configured for this level.";
      case "whitelist":
        return "Only specifically approved apps can be installed. All other apps are blocked regardless of category.";
      case "blacklist":
        return "Apple App Store allows unrestricted app downloads. Only blacklisted apps are blocked.";
    }
  };

  return (
    <>
      <div className="space-y-6">
        <div>
          <h2 className="text-xl font-semibold text-foreground">App Settings</h2>
          <p className="text-muted-foreground mt-1">Configure app store and installation preferences for this level</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Store className="w-5 h-5" />
              App Mode Selection
            </CardTitle>
            <CardDescription>
              Choose how apps are filtered and managed for devices on this level
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <RadioGroup
                value={appMode}
                onValueChange={handleModeChange}
                className="space-y-3"
              >
                <div className="flex items-start gap-3 p-3 rounded-lg border">
                  <RadioGroupItem value="categories" id="level-app-categories" />
                  <div className="flex-1">
                    <Label htmlFor="level-app-categories" className="flex items-center gap-2 font-medium cursor-pointer">
                      <Store className="w-4 h-4" />
                      Categories (Kosher App Store)
                    </Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      Filter apps based on category rules. Access to whitelist, blacklist, and category settings.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-3 rounded-lg border">
                  <RadioGroupItem value="whitelist" id="level-app-whitelist" />
                  <div className="flex-1">
                    <Label htmlFor="level-app-whitelist" className="flex items-center gap-2 font-medium cursor-pointer">
                      <CheckCircle className="w-4 h-4" />
                      Whitelist Only
                    </Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      Only allow specifically approved apps. All other apps are blocked.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-3 rounded-lg border">
                  <RadioGroupItem value="blacklist" id="level-app-blacklist" />
                  <div className="flex-1">
                    <Label htmlFor="level-app-blacklist" className="flex items-center gap-2 font-medium cursor-pointer">
                      <Shield className="w-4 h-4" />
                      Blacklist (Apple App Store)
                    </Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      Full access to all iOS apps. Only specifically blocked apps are restricted.
                    </p>
                  </div>
                </div>
              </RadioGroup>

              <div className="p-3 bg-primary/10 rounded-lg border border-primary/20">
                <p className="text-sm text-primary">
                  <strong>Current Mode:</strong> {getModeDescription(appMode)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Blacklist Mode Warning Dialog */}
      <AlertDialog open={showBlacklistWarning} onOpenChange={setShowBlacklistWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Switch to Apple App Store?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to do that? This will allow users on this level to download whatever app they want. 
              All current app restrictions and category filters will be bypassed for all devices on this level.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setShowBlacklistWarning(false);
              setPendingMode(null);
            }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmBlacklistSwitch}>
              Yes, Switch to Apple App Store
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
